import { useState } from "react";
import { Image, View } from "react-native";
import {
  CalendarClock,
  Egg,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react-native";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { MoonEmptyState, MoonSkeleton } from "@louise/moon-ui";
import {
  useFrozenSpecimens,
  useOocyteCohorts,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  buildPostCycleInventorySnapshot,
  resolvePostCycleSectionState,
} from "@src/packages/cycles/presenters/postCycleState";
import { useTranslations } from "@src/packages/i18n";
import {
  projectFertilityPrediction,
  type ObservedValue,
} from "@src/packages/fertility-projections";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "./OutcomeDotFunnelRows";
import { FunnelScopeTabs, type FunnelScopeTabOption } from "./FunnelScopeTabs";
import type { PlanAction } from "../plan.types";
import { CarePillActionButton } from "./CarePillActionButton";

type TabId = "this-cycle" | "all-eggs" | "all-embryos";

const cryoVaultFridgeIllustration =
  require("@src/apps/moon/assets/images/cryo-vault-fridge.png") as number;

function eggFunnelStages(
  start: number,
  sourceCycleId?: string,
): OutcomeDotFunnelStage[] {
  const frozen: ObservedValue<number> = {
    value: start,
    provenance: {
      sourceType: "clinic_record",
      sourceId: sourceCycleId ?? "cryo-vault-total",
    },
  };
  const projection = projectFertilityPrediction({
    kind: "frozen_oocyte_cohort",
    patient: {},
    oocytes: { frozen, sourceCycleId },
    goal: { kind: "expected_stage_count", stage: "live_births" },
  });
  const value = (id: string) =>
    Math.max(
      0,
      Math.round(
        projection.stages.find((stage) => stage.id === id)?.estimate.mean ?? 0,
      ),
    );
  const hasStage = (id: string) =>
    projection.stages.some((stage) => stage.id === id);
  const surviveThaw = value("thawed_oocytes");
  const fertilize = value("fertilized_oocytes");
  const blastocysts = value("blastocysts");
  const euploids = value("euploid_embryos");
  const liveBirthProbability =
    projection.probabilityAtLeastOneLiveBirth?.mean ?? null;
  return [
    {
      id: "frozen-eggs",
      label: "Frozen eggs",
      value: start,
      detail: "Stored now.",
    },
    {
      id: "survive-thaw",
      label: "Survive thaw",
      value: surviveThaw,
      detail: `${Math.max(0, start - surviveThaw)} may not survive thawing in the general baseline.`,
      missing: !hasStage("thawed_oocytes"),
    },
    {
      id: "fertilize",
      label: "Fertilize",
      value: fertilize,
      detail: `${Math.max(0, surviveThaw - fertilize)} may not fertilize in the general baseline.`,
      missing: !hasStage("fertilized_oocytes"),
    },
    {
      id: "blastocysts",
      label: "Blastocysts",
      value: blastocysts,
      detail: `${Math.max(0, fertilize - blastocysts)} may stop before blastocyst stage in the general baseline.`,
      missing: !hasStage("blastocysts"),
    },
    {
      id: "euploids",
      label: "Euploid embryos",
      value: euploids,
      detail: `${Math.max(0, blastocysts - euploids)} may be aneuploid or untested in the general baseline.`,
      missing: !hasStage("euploid_embryos"),
    },
    {
      id: "live-birth",
      label: "Live birth estimate",
      value:
        liveBirthProbability == null
          ? 0
          : Math.max(0, Math.round(euploids * liveBirthProbability)),
      displayValue:
        liveBirthProbability == null
          ? undefined
          : `${Math.round(liveBirthProbability * 100)}%`,
      detail:
        "General population baseline; age and clinic outcomes can change this.",
      missing: liveBirthProbability == null,
    },
  ];
}

function embryoFunnelStages(
  start: number,
  sourceCycleId?: string,
): OutcomeDotFunnelStage[] {
  const frozen: ObservedValue<number> = {
    value: start,
    provenance: {
      sourceType: "clinic_record",
      sourceId: sourceCycleId ?? "cryo-vault-total",
    },
  };
  const projection = projectFertilityPrediction({
    kind: "embryo_transfer",
    patient: {},
    embryos: { frozen, sourceCycleId },
    goal: { kind: "expected_stage_count", stage: "live_births" },
  });
  const value = (id: string) =>
    Math.max(
      0,
      Math.round(
        projection.stages.find((stage) => stage.id === id)?.estimate.mean ?? 0,
      ),
    );
  const hasStage = (id: string) =>
    projection.stages.some((stage) => stage.id === id);
  const surviveThaw = value("warmed_embryos");
  const transferred = value("transferred_embryos");
  const implanted = value("implanted_embryos");
  const liveBirthProbability =
    projection.probabilityAtLeastOneLiveBirth?.mean ?? null;
  return [
    {
      id: "frozen-embryos",
      label: "Frozen embryos",
      value: start,
      detail: "Stored now.",
    },
    {
      id: "survive-thaw",
      label: "Survive thaw",
      value: surviveThaw,
      detail: `${Math.max(0, start - surviveThaw)} may not survive warming in the general baseline.`,
      missing: !hasStage("warmed_embryos"),
    },
    {
      id: "transferred",
      label: "Transferred",
      value: transferred,
      detail: "The baseline treats warmed embryos as available for transfer.",
      missing: !hasStage("transferred_embryos"),
    },
    {
      id: "implanted",
      label: "Implantation estimate",
      value: implanted,
      detail: `${Math.max(0, transferred - implanted)} may not implant in the general baseline.`,
      missing: !hasStage("implanted_embryos"),
    },
    {
      id: "live-birth",
      label: "Live birth estimate",
      value:
        liveBirthProbability == null
          ? 0
          : Math.max(0, Math.round(transferred * liveBirthProbability)),
      displayValue:
        liveBirthProbability == null
          ? undefined
          : `${Math.round(liveBirthProbability * 100)}%`,
      detail:
        "General population baseline; embryo grade, PGT status, and clinic outcomes can change this.",
      missing: liveBirthProbability == null,
    },
  ];
}

function FutureUseEmptyState({
  primaryUnit,
  totalEggs,
  totalEmbryos,
  totalSperm,
  onAction,
  state = "empty",
}: {
  primaryUnit: "eggs" | "embryos";
  totalEggs: number;
  totalEmbryos: number;
  totalSperm: number;
  onAction?: (action: PlanAction) => void;
  state?: "empty" | "error" | "partial";
}) {
  const t = useTranslations();
  const hasOtherStoredUnit =
    primaryUnit === "eggs" ? totalEmbryos > 0 : totalEggs > 0;
  const hasAnyStoredSpecimens =
    totalEggs > 0 || totalEmbryos > 0 || totalSperm > 0;
  const selectedLabel = primaryUnit === "eggs" ? "eggs" : "embryos";
  const otherLabel = primaryUnit === "eggs" ? "embryos" : "eggs";
  const title =
    state === "error"
      ? t("homepage.review_concept.future_use_vault_unavailable_title")
      : state === "partial"
        ? t("homepage.review_concept.future_use_vault_partial_title")
        : !hasAnyStoredSpecimens
          ? "Your vault is empty"
          : `No frozen ${selectedLabel} yet`;
  const subtitle =
    state === "error"
      ? t("homepage.review_concept.future_use_vault_unavailable_subtitle")
      : state === "partial"
        ? t("homepage.review_concept.future_use_vault_partial_subtitle")
        : !hasAnyStoredSpecimens
          ? "Add frozen eggs or embryos to see a future-use projection. Past cycles can add recorded outcomes when they are linked."
          : hasOtherStoredUnit
            ? `Your vault has frozen ${otherLabel}, but this projection starts with frozen ${selectedLabel}.`
            : totalSperm > 0
              ? "Your vault has stored sperm, but this view starts with frozen eggs or embryos."
              : `Add frozen ${selectedLabel} to see a future-use projection. Past cycles can add recorded outcomes when they are linked.`;
  const action = onAction ? (
    <View
      style={{
        width: "100%",
        maxWidth: 420,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <CarePillActionButton
        icon={Plus}
        label="Add specimens"
        variant="primary"
        surface="selected"
        flexGrow={1}
        flexBasis={0}
        onPress={() =>
          onAction({ label: "Add specimens", screen: "CryoVaultScreen" })
        }
      />
      <CarePillActionButton
        icon={CalendarClock}
        label="View past cycles"
        variant="secondary"
        flexGrow={1}
        flexBasis={0}
        onPress={() =>
          onAction({ label: "View past cycles", screen: "AllPastCycles" })
        }
      />
    </View>
  ) : null;

  return (
    <View style={{ ...GLASS_CARD_STYLE, padding: 16, overflow: "visible" }}>
      <MoonEmptyState
        illustration={
          <Image
            source={cryoVaultFridgeIllustration}
            resizeMode="contain"
            accessibilityLabel=""
            style={{ width: 190, height: 160 }}
          />
        }
        title={title}
        subtitle={subtitle}
        action={action}
        testID="future-use-empty-state"
        style={{ paddingVertical: 24, paddingHorizontal: 0 }}
      />
    </View>
  );
}

export function FutureUseEstimateTabs({
  primaryUnit = "eggs",
  sourceCycleId,
  onAction,
}: {
  primaryUnit?: "eggs" | "embryos";
  /** The cycle whose stored specimens should back the cycle-scoped tab. */
  sourceCycleId?: string | null;
  onAction?: (action: PlanAction) => void;
}) {
  const frozen = useFrozenSpecimens();
  const cohortsQuery = useOocyteCohorts();
  const { specimens, isLoading, error } = frozen;
  const cohorts = cohortsQuery.data;
  const vaultInventory = buildPostCycleInventorySnapshot(specimens, cohorts, {
    kind: "vault",
  });
  const cycleInventory = sourceCycleId
    ? buildPostCycleInventorySnapshot(specimens, cohorts, {
        kind: "origin_cycle",
        cycleId: sourceCycleId,
      })
    : null;
  const totalEggs = vaultInventory.counts.oocyte;
  const totalEmbryos = vaultInventory.counts.embryo;
  const totalSperm = vaultInventory.counts.semen;
  const actualSelectedCount = primaryUnit === "eggs" ? totalEggs : totalEmbryos;
  const thisCycleEggs = cycleInventory?.counts.oocyte ?? 0;
  const thisCycleEmbryos = cycleInventory?.counts.embryo ?? 0;
  const hasCycleInventory =
    sourceCycleId != null &&
    (primaryUnit === "embryos" ? thisCycleEmbryos > 0 : thisCycleEggs > 0);
  const [active, setActive] = useState<TabId>("this-cycle");
  const hasData = (specimens?.length ?? 0) > 0 || (cohorts?.length ?? 0) > 0;
  const sectionState = resolvePostCycleSectionState({
    isLoading: isLoading || cohortsQuery.isLoading,
    isError: !!error || !!cohortsQuery.error,
    hasData,
  });

  if (sectionState === "loading") {
    return (
      <View
        testID="future-use-loading-state"
        style={{ gap: 12, paddingTop: 8, paddingBottom: 24 }}
      >
        <MoonSkeleton width="100%" height={38} radius={16} />
        <MoonSkeleton width="100%" height={208} radius={16} />
      </View>
    );
  }

  if (sectionState === "error" || sectionState === "partial") {
    return (
      <FutureUseEmptyState
        primaryUnit={primaryUnit}
        totalEggs={totalEggs}
        totalEmbryos={totalEmbryos}
        totalSperm={totalSperm}
        onAction={onAction}
        state={sectionState}
      />
    );
  }

  if (actualSelectedCount === 0) {
    return (
      <FutureUseEmptyState
        primaryUnit={primaryUnit}
        totalEggs={totalEggs}
        totalEmbryos={totalEmbryos}
        totalSperm={totalSperm}
        onAction={onAction}
      />
    );
  }

  const options: FunnelScopeTabOption<TabId>[] =
    primaryUnit === "embryos"
      ? [
          ...(hasCycleInventory
            ? [
                {
                  id: "this-cycle" as const,
                  label: "From this cycle",
                  icon: RefreshCw,
                },
              ]
            : []),
          { id: "all-embryos", label: "From all my embryos", icon: Sparkles },
        ]
      : [
          ...(hasCycleInventory
            ? [
                {
                  id: "this-cycle" as const,
                  label: "From this cycle",
                  icon: RefreshCw,
                },
              ]
            : []),
          { id: "all-eggs", label: "From all my eggs", icon: Egg },
          ...(totalEmbryos > 0
            ? [
                {
                  id: "all-embryos" as const,
                  label: "From all my embryos",
                  icon: Sparkles,
                },
              ]
            : []),
        ];

  const safeActive = options.some((option) => option.id === active)
    ? active
    : (options[0]?.id ?? "all-eggs");

  const stages =
    safeActive === "all-embryos"
      ? embryoFunnelStages(totalEmbryos)
      : safeActive === "all-eggs"
        ? eggFunnelStages(totalEggs)
        : primaryUnit === "embryos"
          ? embryoFunnelStages(thisCycleEmbryos, sourceCycleId ?? undefined)
          : eggFunnelStages(thisCycleEggs, sourceCycleId ?? undefined);

  return (
    <View>
      <FunnelScopeTabs
        options={options}
        active={safeActive}
        onChange={setActive}
      />
      <OutcomeDotFunnelRows
        stages={stages}
        totalDots={stages[0]?.value ?? 1}
        attachedTop
      />
    </View>
  );
}
